import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountSummaryData,
  GET_ACCOUNT_SUMMARY
} from '../queries/accountSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountSummaryData;
  isDate?: boolean;
}

const RELATIONSHIP_FIELDS: FieldMapping[] = [
  { apiField: 'relationshipType', formField: 'relationshipType' },
  { apiField: 'fullRelationshipType', formField: 'fullRelationshipType' },
  { apiField: 'relationshipPriority', formField: 'relationshipPriority' },
  {
    apiField: 'suggestedRelationshipType',
    formField: 'suggestedRelationshipType'
  },
  {
    apiField: 'miscategorizedRelationshipType',
    formField: 'miscategorizedRelationshipType'
  },
  {
    apiField: 'miscategorizedRelationshipTypeRequestDate',
    formField: 'miscategorizedRelationshipTypeRequestDate',
    isDate: true
  },
  {
    apiField: 'miscategorizedRelationshipTypeUpdateDate',
    formField: 'miscategorizedRelationshipTypeUpdateDate',
    isDate: true
  }
];

// Convert ISO date string to YYYY-MM-DD format for input
const toDateInputValue = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
};

function getChangedValues(
  fields: FieldMapping[],
  current: AccountSummaryData,
  initial: AccountSummaryData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      changes[apiField] =
        currentVal === '' ? null : isDate ? currentVal || null : currentVal;
    }
  }

  return changes;
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        relationshipType
        fullRelationshipType
        relationshipPriority
        suggestedRelationshipType
        miscategorizedRelationshipType
        miscategorizedRelationshipTypeRequestDate
        miscategorizedRelationshipTypeUpdateDate
      }
    }
  }
`;

export interface EditRelationshipStrengthsDialogProps {
  accountId: string;
  initialValue: AccountSummaryData;
  isOpen: boolean;
  onChange: (data: AccountSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountSummaryData;
}

export function EditRelationshipStrengthsDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditRelationshipStrengthsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_SUMMARY, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(RELATIONSHIP_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountSummaryData>(
    field: K,
    fieldValue: AccountSummaryData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save relationship strengths');
      } else {
        toast.success('Relationship strengths saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving relationship strengths:', error);
      toast.error('Failed to save relationship strengths');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Relationship Strengths"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditDynamicSelectField
          fieldName="relationship_type"
          label="Relationship Type"
          model="opstream.account"
          onChange={v => {
            updateField('relationshipType', v);
          }}
          value={value.relationshipType}
        />
        <EditFormField
          label="Full Relationship Type"
          onChange={v => {
            updateField('fullRelationshipType', v);
          }}
          value={value.fullRelationshipType}
        />

        <EditDynamicSelectField
          fieldName="relationship_priority"
          label="Relationship Priority"
          model="opstream.account"
          onChange={v => {
            updateField('relationshipPriority', v);
          }}
          value={value.relationshipPriority}
        />
        <EditDynamicSelectField
          fieldName="suggested_relationship_type"
          label="Suggested Relationship Type"
          model="opstream.account"
          onChange={v => {
            updateField('suggestedRelationshipType', v);
          }}
          value={value.suggestedRelationshipType}
        />
        <EditCheckboxField
          checked={value.miscategorizedRelationshipType}
          label="Miscategorized Relationship Type"
          onCheckedChange={checked => {
            updateField('miscategorizedRelationshipType', checked);
          }}
        />
        <EditFormField
          label="Miscategorized Relationship Type Request Date"
          onChange={v => {
            updateField('miscategorizedRelationshipTypeRequestDate', v);
          }}
          placeholder="Select date"
          type="date"
          value={toDateInputValue(
            value.miscategorizedRelationshipTypeRequestDate
          )}
        />
        <EditFormField
          label="Miscategorized Relationship Type Update Date"
          onChange={v => {
            updateField('miscategorizedRelationshipTypeUpdateDate', v);
          }}
          placeholder="Select date"
          type="date"
          value={toDateInputValue(
            value.miscategorizedRelationshipTypeUpdateDate
          )}
        />
      </div>
    </EditDialog>
  );
}
